import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/services/api";
import toast from "react-hot-toast";
import { X, ArrowRight } from "lucide-react";

export default function PaymentAllocationForm({ paymentId, onClose, onSuccess }) {
  const [allocations, setAllocations] = useState([]);

  const { data: payment, isLoading: paymentLoading } = useQuery({
    queryKey: ["payment", paymentId],
    queryFn: async () => (await api.get(`/payments/${paymentId}`)).data,
    enabled: !!paymentId,
  });

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ["invoices", payment?.organization_id, payment?.customer_id],
    queryFn: async () => (await api.get("/invoices", { 
      params: { 
        organization_id: payment?.organization_id, 
        customer_id: payment?.customer_id,
        status: "pending,overdue"
      } 
    })).data,
    enabled: !!payment?.organization_id && !!payment?.customer_id,
  });

  const allocatePayment = useMutation({
    mutationFn: async (allocation) => {
      return (await api.post(`/payments/${paymentId}/allocate`, allocation)).data;
    },
    onSuccess: () => {
      toast.success("Payment allocated successfully");
      onSuccess();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Failed to allocate payment"),
  });

  const handleAddAllocation = () => {
    setAllocations([...allocations, { invoice_id: "", allocated_amount: "" }]);
  };

  const handleRemoveAllocation = (index) => {
    setAllocations(allocations.filter((_, i) => i !== index));
  };

  const handleAllocationChange = (index, field, value) => {
    const newAllocations = [...allocations];
    newAllocations[index][field] = value;
    setAllocations(newAllocations);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (allocations.length === 0) {
      toast.error("Please add at least one allocation");
      return;
    }

    const totalAllocated = allocations.reduce((sum, a) => sum + parseFloat(a.allocated_amount || 0), 0);
    const availableAmount = parseFloat(payment?.amount || 0) - parseFloat(payment?.allocations?.reduce((sum, a) => sum + parseFloat(a.allocated_amount || 0), 0) || 0);

    if (totalAllocated > availableAmount) {
      toast.error(`Total allocation (₹${totalAllocated}) exceeds available amount (₹${availableAmount})`);
      return;
    }

    // Submit allocations one by one
    allocations.forEach((allocation) => {
      if (allocation.invoice_id && allocation.allocated_amount) {
        allocatePayment.mutate(allocation);
      }
    });
  };

  if (paymentLoading || invoicesLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-lg p-6">
          <div className="text-center text-slate-500">Loading…</div>
        </div>
      </div>
    );
  }

  const availableAmount = parseFloat(payment?.amount || 0) - parseFloat(payment?.allocations?.reduce((sum, a) => sum + parseFloat(a.allocated_amount || 0), 0) || 0);
  const currentTotalAllocated = allocations.reduce((sum, a) => sum + parseFloat(a.allocated_amount || 0), 0);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#121214] rounded-sm w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
          <h2 className="text-2xl font-extrabold tracking-tighter">Allocate Payment</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        
        <div className="p-6">
          <div className="mb-6 p-4 bg-slate-50 dark:bg-slate-900 rounded-sm">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">Payment Amount:</span>
              <span className="font-semibold">₹{Number(payment?.amount || 0).toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-slate-600 dark:text-slate-400">Already Allocated:</span>
              <span className="font-semibold">₹{Number(payment?.allocations?.reduce((sum, a) => sum + parseFloat(a.allocated_amount || 0), 0) || 0).toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-200 dark:border-slate-700">
              <span className="text-sm font-semibold text-slate-600 dark:text-slate-400">Available to Allocate:</span>
              <span className="font-bold text-lg text-[#002FA7]">₹{availableAmount.toLocaleString("en-IN")}</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-3">
              {allocations.map((allocation, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="flex-1">
                    <label className="block">
                      <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Invoice</span>
                      <select
                        value={allocation.invoice_id}
                        onChange={(e) => handleAllocationChange(index, "invoice_id", e.target.value)}
                        className="mt-1 w-full border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 rounded-sm"
                        required
                      >
                        <option value="">Select invoice…</option>
                        {invoices.map((i) => (
                          <option key={i.id} value={i.id}>
                            {i.invoice_number} - ₹{Number(i.total).toLocaleString("en-IN")} ({i.calculated_status})
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="w-32">
                    <label className="block">
                      <span className="uppercase text-[10px] tracking-[0.2em] text-slate-500">Amount</span>
                      <div className="mt-1 relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={allocation.allocated_amount}
                          onChange={(e) => handleAllocationChange(index, "allocated_amount", e.target.value)}
                          className="w-full border border-slate-300 dark:border-slate-700 bg-transparent pl-6 pr-2 py-2 rounded-sm text-sm"
                          placeholder="0.00"
                          required
                        />
                      </div>
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveAllocation(index)}
                    className="mt-6 p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-sm"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={handleAddAllocation}
              className="w-full py-2 border border-dashed border-slate-300 dark:border-slate-700 rounded-sm text-sm text-slate-600 dark:text-slate-400 hover:border-slate-900 dark:hover:border-white"
            >
              + Add Invoice Allocation
            </button>

            {allocations.length > 0 && (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-sm text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-600 dark:text-slate-400">Total to Allocate:</span>
                  <span className="font-semibold">₹{currentTotalAllocated.toLocaleString("en-IN")}</span>
                </div>
                {currentTotalAllocated > availableAmount && (
                  <div className="mt-1 text-red-600 text-xs">
                    Exceeds available amount by ₹{(currentTotalAllocated - availableAmount).toLocaleString("en-IN")}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-800">
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 border border-slate-300 dark:border-slate-700 rounded-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={allocatePayment.isPending || allocations.length === 0 || currentTotalAllocated > availableAmount}
                className="px-5 py-2.5 bg-[#002FA7] text-white rounded-sm hover:bg-[#001F73] disabled:opacity-50 flex items-center gap-2"
              >
                {allocatePayment.isPending ? "Allocating…" : (
                  <>
                    Allocate <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
